import React from "react";
import CSSModules from "lib/utils/css-modules";
import _ from "lodash";

import { OrderInstallmentDecorator, PaymentProfileInstallmentDecorator } from "lib/decorators/index";
import st from "lib/utils/index";
import AppSettings from "lib/services/AppSettings";
import * as FormControl from "lib/components/FormControl";
import styles from "./InstallmentFormSection.less";

@CSSModules(styles)
export default class InstallmentFormSection extends React.PureComponent {
  _getInstallmentDecorator() {
    const { parent } = this.props;
    if (parent.type === "Order") {
      return OrderInstallmentDecorator;
    } else if (parent.type === "PaymentProfile") {
      return PaymentProfileInstallmentDecorator;
    }
  }

  handlePropChange(prop, value) {
    const { parent, installment, onChange } = this.props;
    const InstallmentDecorator = this._getInstallmentDecorator();
    const nextInstallment = new InstallmentDecorator({
      ...installment,
      [prop]: value,
    });
    onChange(nextInstallment);
  }

  render() {
    const {
      installment,
      parent,
      payments,
      name,
      errors,
      installmentIndex,
      isFirstInstallment,
      isSupplier,
      isLastInstallment,
      onRemove,
      onBlur,
    } = this.props;
    const InstallmentDecorator = this._getInstallmentDecorator();
    const installmentType = parent.type === "Order" ? "order_installment" : "payment_profile_installment";
    const receiverOptions = [
      ["supplier", I18n.humanAttributeName("order_installment", "supplier")],
      ["agent", I18n.humanAttributeName("order_installment", "agent")],
    ];

    const transferTypeOptions = InstallmentDecorator.transfer_type_options.filter(([v, _]) => {
      if (v === "online_payment" && parent.marketplace === true) {
        return isFirstInstallment;
      }
      if (v === "online_payment" && parent.marketplace !== true) {
        return isFirstInstallment && (!isSupplier || (payments && payments.online_payment_access));
      }
      return true;
    });
    const paidByOnTypeOptions = _.compact([
      parent.type === "Order"
        ? ["exact_date", I18n.t("papp.components.InstallmentFormSection.exact_date_label")]
        : undefined,
      !isFirstInstallment
        ? [
            "x_days_after_previous_installment",
            I18n.t("papp.components.InstallmentFormSection.x_days_after_previous_installment_label"),
          ]
        : undefined,
      isFirstInstallment
        ? [
            "x_days_after_order_creation",
            I18n.t("papp.components.InstallmentFormSection.x_days_after_order_creation_label"),
          ]
        : undefined,
      isFirstInstallment
        ? [
            "x_days_after_order_full_approval",
            I18n.t("papp.components.InstallmentFormSection.x_days_after_order_full_approval_label"),
          ]
        : undefined,
      [
        "x_days_before_stock_starts_at",
        I18n.t("papp.components.InstallmentFormSection.x_days_before_stock_starts_at_label"),
      ],
      ["on_stock_starts_at", I18n.t("papp.components.InstallmentFormSection.on_stock_starts_at_label")],
    ]);
    const hideReceiverInput = !!(parent.type === "Order" && !parent.agent);
    const canRemove = !(isFirstInstallment && isLastInstallment && parent.type === "PaymentProfile");
    const exactAmountTypeOptions = [
      ["exact_amount_per_order", I18n.t("attributes.per_order")],
      ["exact_amount_per_item", I18n.t("attributes.per_item")],
      ["exact_amount_per_person", I18n.t("attributes.per_person")],
    ];

    return (
      <div styleName="container">
        <div styleName="row" className="fields">
          <div styleName="row-number">{installmentIndex + 1}.</div>
          {!hideReceiverInput && (
            <FormControl.SelectFormGroup
              {...FormControl.addProps(`${name}.receiver`, { errors })}
              value={installment.receiver}
              onChange={this.handlePropChange.bind(this, "receiver")}
              onBlur={onBlur}
              label={I18n.t("papp.components.InstallmentFormSection.to")}
              options={receiverOptions}
              className="u-mediumWidth"
            />
          )}

          <FormControl.FormGroup
            {...FormControl.addProps(`${name}.amount`, { errors })}
            label={I18n.t("papp.components.InstallmentFormSection.how_much")}
          >
            <div className="fields inline">
              {!isLastInstallment && (
                <FormControl.Money
                  {...FormControl.addProps(`${name}.amount_or_percentage`, { errors })}
                  value={installment.amount_or_percentage}
                  onChange={this.handlePropChange.bind(this, "amount_or_percentage")}
                  onBlur={onBlur}
                  min={0}
                  currencies={
                    installment.transfer_type === "online_payment" ? AppSettings.online_payment_currencies : undefined
                  }
                  withPercentageCurrency={parent.type !== "Order"}
                  autoFocus={installment._focus}
                />
              )}

              {!isLastInstallment &&
                installment.type === "PaymentProfileInstallment" &&
                installment.is_exact_amount && (
                  <FormControl.SelectFormGroup
                    {...FormControl.addProps(`${name}.amount_type`, { errors })}
                    value={installment.amount_type}
                    onChange={this.handlePropChange.bind(this, "amount_type")}
                    onBlur={onBlur}
                    options={exactAmountTypeOptions}
                    className="u-mediumWidth u-marginLeft"
                  />
                )}

              {isLastInstallment && parent.type === "Order" && (
                <div className="ui input stFakeMoney">
                  <input
                    type="text"
                    value={
                      parent.remaining_installment_amount_sum.length > 0
                        ? st.formatMoney(parent.remaining_installment_amount_sum)
                        : "-"
                    }
                    readOnly
                  />
                </div>
              )}

              {isLastInstallment && parent.type === "PaymentProfile" && (
                <div className="ui input u-inputLineHeight">
                  {isFirstInstallment
                    ? I18n.t("papp.components.InstallmentFormSection.whole_amount")
                    : I18n.t("papp.components.InstallmentFormSection.remaining_amount")}
                </div>
              )}
            </div>
          </FormControl.FormGroup>

          <FormControl.SelectFormGroup
            {...FormControl.addProps(`${name}.transfer_type`, { errors })}
            value={installment.transfer_type}
            onChange={this.handlePropChange.bind(this, "transfer_type")}
            onBlur={onBlur}
            label={I18n.t("papp.components.InstallmentFormSection.how")}
            options={transferTypeOptions}
            className="u-mediumWidth"
          />

          {installment.transfer_type !== "online_payment" && (
            <FormControl.FormGroup
              {...FormControl.addProps(`${name}.paid_by_type`, { errors })}
              label={I18n.t("papp.components.InstallmentFormSection.when")}
            >
              <div className="fields inline">
                {installment.paid_by_type !== "exact_date" && installment.paid_by_type !== "on_stock_starts_at" && (
                  <FormControl.TimeDuration
                    value={installment.paid_by_time_duration}
                    onChange={this.handlePropChange.bind(this, "paid_by_time_duration")}
                    onBlur={onBlur}
                    required
                    durationTypeTimeLengths={[3600, 86400]}
                    className="u-marginRight"
                  />
                )}

                <FormControl.Select
                  {...FormControl.addProps(`${name}.paid_by_type`, { errors })}
                  value={installment.paid_by_type}
                  onChange={this.handlePropChange.bind(this, "paid_by_type")}
                  onBlur={onBlur}
                  required
                  options={paidByOnTypeOptions}
                />

                {installment.paid_by_type === "exact_date" && (
                  <FormControl.DateTime
                    value={installment.paid_by_at}
                    onChange={this.handlePropChange.bind(this, "paid_by_at")}
                    onBlur={onBlur}
                    required
                    initialTime="23:59"
                    className="u-marginLeft"
                  />
                )}
              </div>
            </FormControl.FormGroup>
          )}

          {canRemove && (
            <FormControl.DeleteRowButton
              onClick={() => {
                onRemove && onRemove();
                onBlur && onBlur();
              }}
            />
          )}
        </div>
        {!parent.marketplace &&
          payments &&
          payments.online_payment_access &&
          installment.transfer_type === "online_payment" && (
            <div style={{ margin: "0 0 5px 0" }}>
              <b>
                <p>
                  Integrated service:{" "}
                  <span style={{ color: "green" }}>{payments.paypal_access ? "PayPal" : "DotPay"}</span>{" "}
                </p>
              </b>
            </div>
          )}
        {parent.type === "Order" && installment.transfer_type === "bank_wire" && (
          <div styleName="installment-additional_options" className="fields inline">
            <FormControl.FormGroup
              label={I18n.humanAttributeName("payment_profile_installment", "account_description")}
            >
              <div className="ui input">{installment.account_description}</div>
            </FormControl.FormGroup>
          </div>
        )}

        {isFirstInstallment &&
          (installment.transfer_type === "online_payment" ||
            (parent.type === "PaymentProfile" && parent.marketplace)) && (
            <div styleName="installment-additional_options" className="fields inline">
              {isFirstInstallment && parent.type === "PaymentProfile" && parent.marketplace && (
                <FormControl.CheckboxFormGroup
                  value={installment.increase_amount_by_marketplace_service_fee}
                  onChange={this.handlePropChange.bind(this, "increase_amount_by_marketplace_service_fee")}
                  onBlur={onBlur}
                  label={I18n.humanAttributeName(
                    "payment_profile_installment",
                    "increase_amount_by_marketplace_service_fee"
                  )}
                  toggle
                />
              )}

              {isFirstInstallment && parent.type === "PaymentProfile" && parent.marketplace && (
                <FormControl.CheckboxFormGroup
                  value={installment.increase_amount_by_marketplace_commision}
                  onChange={this.handlePropChange.bind(this, "increase_amount_by_marketplace_commision")}
                  onBlur={onBlur}
                  label={I18n.humanAttributeName(
                    "payment_profile_installment",
                    "increase_amount_by_marketplace_commision"
                  )}
                  toggle
                />
              )}

              {installment.confirmation_installment && (
                <FormControl.CheckboxFormGroup
                  value={!installment.paid_after_company_approval}
                  onChange={(v) => this.handlePropChange("paid_after_company_approval", !v)}
                  onBlur={onBlur}
                  label={I18n.t("papp.components.InstallmentFormSection.paid_after_company_approval_label")}
                  toggle
                />
              )}
            </div>
          )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/lib/components/FormSection/InstallmentFormSection.jsx